import {ChangeDetectionStrategy, Component, computed, input, output, signal} from '@angular/core';
import {Card} from 'primeng/card';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {Tooltip} from 'primeng/tooltip';
import {TranslocoPipe} from '@jsverse/transloco';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {AttributeConsistencyStatus, AuditInfo, TenderPhaseType} from '@frontend/shared-api';
import {ModulePanelVM, ModuleTab} from './ec3-module-group.types';
import {SubCriteriaTabComponent} from '../../../../../../../shared/components/sub-criteria-tab/sub-criteria-tab.component';
import {
  AttributeConsistencyChange,
  AttributeScoreSelection,
  SubCriteriaPanelVM,
} from '../../../../../../../shared/components/sub-criteria-tab/sub-criteria-tab.types';
import {ATTRIBUTE_CONSISTENCY_STATUS_COLORS} from '../../../../../../../shared/constants/attribute-consistency.constants';
import {aggregateTabStatus} from '../tab-status';
import {TabStatusDirective} from '../../../../../../../shared/tabs/tab-status.directive';
import {logTabRender} from '../../../../../../../shared/tabs/tab-render-log.util';
import {TabsUnsavedRevealDirective} from '../../../../../../../shared/tabs/tabs-unsaved-reveal.directive';

@Component({
  selector: 'app-ec3-module-group',
  imports: [
    Card,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Tooltip,
    TranslocoPipe,
    TranslateDirectusPipe,
    SubCriteriaTabComponent,
    TabStatusDirective,
    TabsUnsavedRevealDirective,
  ],
  templateUrl: './ec3-module-group.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Ec3ModuleGroupComponent {
  readonly moduleTabs = input.required<ModuleTab[]>();
  readonly subCriteriaPanels = input.required<SubCriteriaPanelVM[]>();
  readonly disabled = input.required<boolean>();
  readonly projectId = input.required<number>();
  readonly showConsistency = input(false);
  readonly lastSaveAuditInfo = input<AuditInfo | null>(null);

  readonly scoreSelected = output<AttributeScoreSelection>();
  readonly save = output<number>();
  readonly consistencyChanged = output<AttributeConsistencyChange>();

  protected readonly phase = TenderPhaseType.RFP;
  protected readonly consistencyWarningColor =
    ATTRIBUTE_CONSISTENCY_STATUS_COLORS[AttributeConsistencyStatus.REVIEW_REQUIRED];
  protected readonly consistencyClarificationColor =
    ATTRIBUTE_CONSISTENCY_STATUS_COLORS[AttributeConsistencyStatus.CLARIFICATION_REQUIRED];

  protected readonly activeModuleTabIndex = signal(0);
  protected readonly activeSubCriteriaTabIndex = signal(0);
  protected readonly openedModuleTabIndexes = signal(new Set<number>([0]));

  protected readonly moduleGroups = computed<ModulePanelVM[]>(() => {
    const panelsByDirectusId = new Map(
      this.subCriteriaPanels().map((panel) => [panel.directusId, panel]),
    );

    return this.moduleTabs()
      .map((module) => {
        const panels = module.subCriteria
          .map((subCriteria) => panelsByDirectusId.get(subCriteria.directusId))
          .filter((panel): panel is SubCriteriaPanelVM => panel !== undefined);

        return {
          moduleId: module.moduleId,
          name: module.moduleName,
          translations: module.moduleTranslations,
          status: aggregateTabStatus(panels.map((panel) => panel.status)),
          panels: panels,
        };
      })
      .filter((module) => module.panels.length > 0);
  });

  protected readonly hasModules = computed<boolean>(() => {
    return this.moduleGroups().length > 0;
  });

  onModuleSelected(value: string | number): void {
    const index = typeof value === 'string' ? Number.parseInt(value, 10) : value;

    this.activeModuleTabIndex.set(index);
    this.activeSubCriteriaTabIndex.set(0);
    this.openedModuleTabIndexes.set(new Set(this.openedModuleTabIndexes()).add(index));

    logTabRender('M', index);
  }

  onSubCriteriaSelected(value: string | number): void {
    this.activeSubCriteriaTabIndex.set(
      typeof value === 'string' ? Number.parseInt(value, 10) : value,
    );

    logTabRender('SC', value);
  }

  onScoreSelected(selection: AttributeScoreSelection): void {
    this.scoreSelected.emit(selection);
  }

  onSave(subCriteriaId: number): void {
    this.save.emit(subCriteriaId);
  }
}
